/**
 * The configurable part of an event: what is competed in, how it is divided into categories, and
 * how it is scored.
 *
 * This is where "configurability is the point" (D14–D21) actually lands. The pilot community's
 * answers are seed data, not schema. Another community must be able to run a completely
 * different programme without a code change, and the two mechanisms that make that true are:
 *
 * - **Dimensions hang off the competition, not the event** (D18). Dance bands by 0–7 / 8–12 /
 *   13–17 / 18+ / 55+; the hand drum special by 0–17 / 18+; the jigging contest by an unbounded
 *   youth/adult. One global Age Group cannot express that.
 * - **Collapse rules make ragged programmes expressible** (D18). A full cross-product of the
 *   sample event's dance dimensions is about seventy categories; the event runs fifteen, because
 *   dance style collapses inside the youth bands. Without a way to say so, "Boy's Combined" has
 *   to be faked as six categories nobody enters.
 */

import { index, integer, sqliteTable, text, uniqueIndex } from "drizzle-orm/sqlite-core";
import { archivable, audited, flag, id, instant, json, ref, sortOrder } from "./columns.js";

/** Ranked, or shown but not ranked. An exhibition pays without being judged (D17). */
export const COMPETITION_TYPES = ["competitive", "exhibition"] as const;

/**
 * How a result is produced. The programme is not only judged dance categories (D19): a pageant,
 * a jigging contest, a hand drum special, a horseshoe tournament, canoe races and a traditional
 * game all need registration, check-in and payouts, and differ only here.
 */
export const COMPETITION_FORMATS = ["judged", "placement_only", "heat_or_bracket"] as const;

/**
 * How a dancer signs up when a competition runs across several sessions (D53) — a separate question
 * from *where* it runs (its sessions and its categories' sessions).
 *
 * - `once`: one entry for the whole competition. You enter a category once; if it runs rounds you
 *   advance through them. The common case, and the only sensible one for a rounds or split-category
 *   competition, so it is the default.
 * - `per_session`: the competition recurs as an independent contest each session it runs in, and a
 *   dancer enters each session separately — an open intertribal danced every block.
 */
export const COMPETITION_ENTRY_MODES = ["once", "per_session"] as const;

/**
 * How somebody comes to be entered in a competition (D75) — chosen, automatic, or both allowed.
 * See `competitions.enrolment`.
 */
export const COMPETITION_ENROLMENTS = ["chosen", "automatic", "either"] as const;

export const competitions = sqliteTable(
  "competitions",
  {
    id: id(),
    eventId: ref("event_id").notNull(),
    communityId: ref("community_id").notNull(),
    name: text("name").notNull(),
    description: text("description"),

    type: text("type", { enum: COMPETITION_TYPES }).notNull().default("competitive"),
    format: text("format", { enum: COMPETITION_FORMATS }).notNull().default("judged"),

    /** How a dancer signs up across sessions (D53) — one entry, or one per session. */
    entryMode: text("entry_mode", { enum: COMPETITION_ENTRY_MODES }).notNull().default("once"),

    startsLocal: text("starts_local"),
    startsAt: instant("starts_at"),
    location: text("location"),

    /** Cap on entries. Null means uncapped, which is the common case. */
    entryLimit: integer("entry_limit"),

    /**
     * The participant unit (D55). Both default to 1, which is an individual competition — no team
     * is ever created and the register-as-a-person path is untouched. `rosterMax > 1` makes it a
     * team competition whose every entry is a `team`, valid only at `rosterMin ≤ members ≤
     * rosterMax`. There is no individual/team flag: the distinction is derived from the bounds so
     * the two can never disagree.
     */
    rosterMin: integer("roster_min").notNull().default(1),
    rosterMax: integer("roster_max").notNull().default(1),

    /**
     * The target panel size for this competition (D56), overriding `events.defaultPanelSize` when
     * set. Null falls back to the event default. A target for the coverage badge and readiness,
     * never a cap on how many judges may be assigned.
     */
    panelSize: integer("panel_size"),

    /**
     * How somebody comes to be in this competition (D75).
     *
     * What makes **Grand Entry a competition** rather than a special case: an exhibition with
     * categories on the age bands, paid by a `participation` rule scoped to them, so the pilot's
     * $40 / $60 / $80 / $100 by band needs no new mechanism and check-in earns it exactly as it
     * earns everything else (D65). What Grand Entry needs that a contest does not is a way in
     * without being asked — everybody dances it, and a desk with three hundred dancers ticking a
     * box three hundred times will forget some, and the ones it forgets are the ones not paid.
     *
     * Three states rather than a flag, because a community asked for all three:
     *
     * - `chosen` — entered by ticking it. Every contest, and the default.
     * - `automatic` — entered by registering for anything else in a session it runs in. Never
     *   offered on the form: a box for something already decided is a box somebody can leave clear
     *   and believe they are out.
     * - `either` — entered automatically *and* offered, for a community that wants the sweep and
     *   still wants a dancer to be able to say which category they are in.
     */
    enrolment: text("enrolment", { enum: COMPETITION_ENROLMENTS }).notNull().default("chosen"),

    /**
     * Whether judges see dancer names or only numbers. Per competition rather than per event,
     * because a community may judge dance blind and a pageant openly (ux.md §3.6).
     */
    blindJudging: flag("blind_judging").notNull().default(false),

    sortOrder: sortOrder(),
    ...audited(),
    ...archivable(),
  },
  (table) => [index("competitions_event_idx").on(table.eventId, table.sortOrder)],
);

/**
 * Which sessions a competition runs in (D50) — its **default** schedule, since a category may name
 * its own session and a round its own, finest level winning (D53). A competition has its own slot
 * within the day, distinct from the session framing it (D20) — jigging in the school gym on the
 * Friday night, horseshoes on the Saturday morning. Empty means it runs throughout / in every
 * session. No `archivable()`: the link lives and dies with the row.
 */
export const competitionSessions = sqliteTable(
  "competition_sessions",
  {
    id: id(),
    competitionId: ref("competition_id").notNull(),
    sessionId: ref("session_id").notNull(),
    communityId: ref("community_id").notNull(),
    ...audited(),
  },
  (table) => [
    uniqueIndex("competition_sessions_unique").on(table.competitionId, table.sessionId),
    index("competition_sessions_session_idx").on(table.sessionId),
  ],
);

/**
 * `nominal` — Gender, Dance Style. `numeric_range` — Age Group, whose values carry min/max so the
 * age on a registration resolves to a band automatically and stays editable.
 */
export const DIMENSION_KINDS = ["nominal", "numeric_range"] as const;

/**
 * A dimension, hanging off a competition — or off nothing at all, which is a **template**.
 *
 * The nullable `competitionId` plus `isTemplate` is the same shape `scoring_configs` uses for the
 * same reason (D43): a community writes "Age Group — 0–7 / 8–12 / 13–17 / 18–54 / 55+" once and
 * starts every subsequent competition from it, without a second table that means almost the same
 * thing.
 *
 * A template is **copied** into a competition, never referenced by it. That is D18 restated: the
 * hand drum special bands 0–17 / 18+ while the dance programme runs five bands, so a dimension
 * shared by reference would recreate exactly the constraint D18 rejected. The template is where a
 * community's usual answer lives; the copy is what that competition actually runs.
 *
 * Every read is by `competitionId`, which a template's null can never match — so a template cannot
 * leak into generation by omission.
 */
export const categoryDimensions = sqliteTable(
  "category_dimensions",
  {
    id: id(),
    /** Null on a template, which belongs to the community rather than to a competition. */
    competitionId: ref("competition_id"),
    communityId: ref("community_id").notNull(),
    name: text("name").notNull(),
    kind: text("kind", { enum: DIMENSION_KINDS }).notNull(),
    required: flag("required").notNull().default(true),
    isTemplate: flag("is_template").notNull().default(false),
    sortOrder: sortOrder(),
    ...audited(),
    ...archivable(),
  },
  (table) => [
    index("category_dimensions_competition_idx").on(table.competitionId, table.sortOrder),
    index("category_dimensions_template_idx").on(table.communityId, table.isTemplate),
  ],
);

export const categoryDimensionValues = sqliteTable(
  "category_dimension_values",
  {
    id: id(),
    dimensionId: ref("dimension_id").notNull(),
    communityId: ref("community_id").notNull(),
    label: text("label").notNull(),

    /** Inclusive bounds for a `numeric_range` dimension. Null at either end means unbounded,
     *  which is how the jigging contest's open-ended Youth / Adults bands are expressed. */
    minValue: integer("min_value"),
    maxValue: integer("max_value"),

    sortOrder: sortOrder(),
    ...audited(),
    ...archivable(),
  },
  (table) => [
    index("category_dimension_values_dimension_idx").on(table.dimensionId, table.sortOrder),
  ],
);

/**
 * "For Age Group in 0–7, 8–12 and 13–17, collapse Dance Style."
 *
 * The result is *Boy's Combined* and *Girl's Combined* as first-class categories rather than six
 * that nobody enters (D18). Generation proposes; the organiser edits the grid before anything is
 * created (ux.md §3.10).
 */
export const categoryCollapseRules = sqliteTable(
  "category_collapse_rules",
  {
    id: id(),
    competitionId: ref("competition_id").notNull(),
    communityId: ref("community_id").notNull(),
    collapsedDimensionId: ref("collapsed_dimension_id").notNull(),
    whenDimensionId: ref("when_dimension_id").notNull(),
    whenValueId: ref("when_value_id").notNull(),
    /** e.g. `{gender} Combined`. Placeholders are dimension names. */
    labelTemplate: text("label_template"),
    ...audited(),
    ...archivable(),
  },
  (table) => [index("category_collapse_rules_competition_idx").on(table.competitionId)],
);

export const CATEGORY_STATUSES = ["draft", "open", "closed", "complete"] as const;

export const categories = sqliteTable(
  "categories",
  {
    id: id(),
    competitionId: ref("competition_id").notNull(),
    communityId: ref("community_id").notNull(),
    name: text("name").notNull(),
    /** True when produced by generation rather than typed. Purely informational — a generated
     *  category the organiser then renamed is still theirs. */
    generated: flag("generated").notNull().default(false),
    entryLimit: integer("entry_limit"),
    status: text("status", { enum: CATEGORY_STATUSES }).notNull().default("draft"),
    /**
     * The session this category runs in (D53), when it runs in a different one from its siblings —
     * "Juniors dance Saturday morning, Adults Saturday afternoon". Null means it follows the
     * competition's own sessions, which is the common case where a whole competition runs together.
     */
    sessionId: ref("session_id"),
    sortOrder: sortOrder(),
    ...audited(),
    ...archivable(),
  },
  (table) => [index("categories_competition_idx").on(table.competitionId, table.sortOrder)],
);

/**
 * Which dimension values a category stands for.
 *
 * A null `valueId` marks the dimension as **collapsed** for that category. That is what lets a
 * payout rule scoped to "Age Group = 0–7" match both the boys' and the girls' category without
 * duplication — the payout is coarser than the category, and the model allows that (D16).
 */
export const categoryDimensionAssignments = sqliteTable(
  "category_dimension_assignments",
  {
    id: id(),
    categoryId: ref("category_id").notNull(),
    dimensionId: ref("dimension_id").notNull(),
    valueId: ref("value_id"),
    communityId: ref("community_id").notNull(),
    ...audited(),
  },
  (table) => [
    // A category may stand for several of a dimension's values (D51) — "Juniors and Under 19, one
    // combined category" — so the row is unique per (category, dimension, value), not per
    // (category, dimension). A collapsed dimension is a single row with a null value.
    uniqueIndex("category_dimension_assignments_unique").on(
      table.categoryId,
      table.dimensionId,
      table.valueId,
    ),
    index("category_dimension_assignments_value_idx").on(table.valueId),
  ],
);

/** Preliminary, final, and whatever a community calls the rounds in between. */
export const phases = sqliteTable(
  "phases",
  {
    id: id(),
    competitionId: ref("competition_id").notNull(),
    communityId: ref("community_id").notNull(),
    name: text("name").notNull(),
    sortOrder: sortOrder(),
    /**
     * The session this round runs in (D53) — round one Saturday, round two Sunday. Null means it
     * follows the category (or, through it, the competition), which is right for rounds that all run
     * in the same block on one day.
     */
    sessionId: ref("session_id"),
    /** How entrants advance from this phase to the next — top N, a score threshold, or all. */
    advancementRuleJson: json("advancement_rule_json"),
    ...audited(),
    ...archivable(),
  },
  (table) => [index("phases_competition_idx").on(table.competitionId, table.sortOrder)],
);

/**
 * `pass_fail` covers eligibility reviews and qualifying rounds where the outcome is a decision,
 * not a number — pass, advance, eligible, disqualified. The decision labels live in
 * `optionsJson`, because which words a community uses is configuration (D34).
 */
export const SCORING_METHODS = ["sum", "average", "drop_high_low", "rank", "pass_fail"] as const;

/**
 * A scoring configuration, attachable at competition, category or phase level, or held as a
 * reusable template with all three null.
 *
 * `version` is stamped onto every result set, which is what keeps a historical result
 * reproducible after the configuration changes (D9).
 */
export const scoringConfigs = sqliteTable(
  "scoring_configs",
  {
    id: id(),
    communityId: ref("community_id").notNull(),
    competitionId: ref("competition_id"),
    categoryId: ref("category_id"),
    phaseId: ref("phase_id"),
    name: text("name").notNull(),
    isTemplate: flag("is_template").notNull().default(false),
    method: text("method", { enum: SCORING_METHODS }).notNull().default("sum"),
    /** Ordered tie-break rules, evaluated in sequence and reported in the calculation trace. */
    tieBreakJson: json("tie_break_json"),
    /** Method-specific options — pass/fail decision labels, rank placement points. Validated by
     *  the Zod schema for the method, so a `sum` config cannot quietly carry rank points. */
    optionsJson: json("options_json"),
    version: integer("version").notNull().default(1),
    ...audited(),
    ...archivable(),
  },
  (table) => [
    index("scoring_configs_community_idx").on(table.communityId, table.isTemplate),
    index("scoring_configs_competition_idx").on(table.competitionId),
    index("scoring_configs_category_idx").on(table.categoryId),
  ],
);

export const scoringCriteria = sqliteTable(
  "scoring_criteria",
  {
    id: id(),
    scoringConfigId: ref("scoring_config_id").notNull(),
    communityId: ref("community_id").notNull(),
    name: text("name").notNull(),
    description: text("description"),
    minValue: integer("min_value").notNull().default(0),
    maxValue: integer("max_value").notNull().default(10),
    /** Step size for the judge's stepper control. Never a slider — ux.md §2. */
    step: integer("step").notNull().default(1),
    /** Weight in basis points (10000 = ×1), so weighting stays integer arithmetic and a
     *  recomputed historical result matches the original exactly. */
    weightBp: integer("weight_bp").notNull().default(10000),
    sortOrder: sortOrder(),
    ...audited(),
    ...archivable(),
  },
  (table) => [index("scoring_criteria_config_idx").on(table.scoringConfigId, table.sortOrder)],
);
